import { useQuery, useQueryClient } from "@tanstack/react-query";

import { margin } from "@/api/margin";
import { useActiveAccount } from "@/components/ActiveAccount";
import { EmptyState, Skeleton } from "@/components/ui";
import { useWsSubscribe } from "@/ws/WebSocketProvider";

/**
 * Margin pane — equity / margin used / margin level / free margin per
 * the active trading account. Color-coded MC and SO bands; the bar
 * widens as the account approaches stop-out.
 */
export function MarginPane() {
  const { active } = useActiveAccount();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["margin", active?.id],
    queryFn: () => margin.get(active!.id),
    enabled: !!active,
    refetchInterval: 5_000,
  });
  useWsSubscribe("fill", () => {
    qc.invalidateQueries({ queryKey: ["margin"] });
  });
  useWsSubscribe("client_tick", () => {
    qc.invalidateQueries({ queryKey: ["margin"] });
  });

  if (!active) {
    return <EmptyState title="No active account" description="Select a trading account from the navbar to see margin." />;
  }
  if (isLoading || !data) {
    return (
      <div className="space-y-2 p-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  const level = data.margin_level_pct == null ? null : Number(data.margin_level_pct);
  const mc = Number(data.mc_level_pct);
  const so = Number(data.so_level_pct);

  let band: "ok" | "warn" | "danger" = "ok";
  if (level != null && level <= so) band = "danger";
  else if (level != null && level <= mc) band = "warn";

  const bandClass =
    band === "danger"
      ? "border-red-700 bg-red-900/30 text-red-200"
      : band === "warn"
        ? "border-amber-700 bg-amber-900/30 text-amber-200"
        : "border-term-border bg-term-elev text-term-fg";

  return (
    <div className="space-y-2 p-2 text-2xs">
      <Row label="Balance" value={data.balance} />
      <Row label="Credit" value={data.credit} />
      <Row label="Equity" value={data.equity} bold />
      <Row label="Margin used" value={data.margin_used} />
      <Row label="Free margin" value={data.free_margin} />
      <div className={`mt-2 rounded-sm border px-2 py-1.5 ${bandClass}`}>
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wider">Margin level</span>
          <span className="font-mono text-sm font-semibold">
            {level == null ? "∞" : `${level.toFixed(2)}%`}
          </span>
        </div>
        <div className="mt-1 text-2xs opacity-80">
          MC ≤ {mc}% · SO ≤ {so}%
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="uppercase tracking-wider text-term-mute">{label}</span>
      <span className={`tnum font-mono ${bold ? "text-term-fg font-semibold" : "text-term-dim"}`}>
        {value}
      </span>
    </div>
  );
}
